import { useOnChangeFilter } from '@/features/shop/components/ProductFilter/interactions/useOnChangeFilter'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { useTrackFilterUtilityClicked } from '@/features/shared/analytics/filters'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { SEARCH_FILTER_TYPE } from '@/features/shared/constants/global'
import { type InputChangeEvent } from '@/features/shared/utils/dataFetching/types'
import { useQueryShoppingStores } from '@/features/shop/services/ShoppingStore/queries'
import { useValidSearchParams } from '@/features/shared/hooks/search'
import { Box, Column, Row } from '@shipt/design-system-layouts'
import { useQueryUniversalSearch } from '@/features/shop/services/UniversalCategories/queries'
import { type GeneralFacetItem } from '@/features/shop/services/Search/types'
import { Skeletons } from '@/features/shared/components/SkeletonLoader'
import { Checkbox } from '@shipt/design-system-checkbox'
import { Body } from '@shipt/design-system-typography'

export const RetailerFilterSection = ({ zip }: { zip: string }) => {
  const storesAvailable = useQueryShoppingStores({ zip }).data?.delivery_stores ?? []
  const { onChangeFilter } = useOnChangeFilter()
  const { data, isPending } = useQueryUniversalSearch({
    contexts: undefined,
  })
  const items: GeneralFacetItem[] =
    data?.facets.filter((f) => f.facet === 'store_id')[0]?.items ?? []
  let storeFacets: number[] = []
  if (items[0]) {
    storeFacets = items[0].prevValue || items.map((s) => parseInt(s.name))
  }
  const { contexts = [] } = useValidSearchParams()

  // The step below is to just show the retailers providing products of selected category in a particular zip
  const storeOptions = storesAvailable.filter((store) => storeFacets.includes(store.id))
  const trackFilterUtilityClicked = useTrackFilterUtilityClicked()

  const changeStore = (
    e: InputChangeEvent,
    contextValue: string,
    name: string,
    filterIndex: number,
  ) => {
    const isChecked = e.target.checked
    let updatedStores: string[] = []
    if (contexts.includes(contextValue)) {
      updatedStores = contexts.filter((context) => context !== contextValue)
    } else {
      updatedStores = [...contexts, contextValue]
    }
    const params = {
      contexts: updatedStores.length ? updatedStores : undefined,
    }
    onChangeFilter(params)

    trackFilterUtilityClicked({
      params,
      filter_active: isChecked,
      filter_value: name,
      list_index: filterIndex,
      utility_type: 'filter',
      display_format: DISPLAY_FORMAT.VERTICAL,
      display_sublocation: `${SEARCH_FILTER_TYPE.STORE}_${DISPLAY_FORMAT.VERTICAL}`,
    })
  }

  if (isPending) {
    return (
      <Column spacing="md" marginTop="sm">
        <Skeletons width={200} height={40} isLoading count={5} />
      </Column>
    )
  }
  return (
    <>
      {storeOptions.map(({ id, context_value = '', name, circular_image }, i) => (
        <Box padding={{ base: ['sm', 0], md: [0, 0, 'sm'] }} marginBottom={{ md: 'xs' }} key={id}>
          <Row align="center" spacing="sm">
            <Checkbox
              name="filter checkbox"
              onChange={(e) => changeStore(e, context_value, name, i)}
              aria-label={name}
              value={context_value}
              checked={contexts.includes(context_value)}
            />
            <Image src={circular_image} width={36} height={36} alt="" />
            <Body>{name}</Body>
          </Row>
        </Box>
      ))}
    </>
  )
}
